// What a derived offer code looks like: the offer's prefix, then the fan's
// account number, optionally zero-filled between the two to a fixed length.
//
// A copy of functions/lib/derivedOfferCode.js, which is the rule the claim path
// actually applies — this one only draws the form's worked example and checks
// the form before it saves. The admin build cannot import from outside src/,
// so the two are kept in step by derivedOfferCode.test.js, which runs both
// against the same table. Why the fill is always zero is explained there.

export const MAX_CODE_LENGTH_DERIVED = 32;
export const MAX_ACCOUNT_DIGITS = 15;
const CODE_PREFIX_PATTERN = /^[A-Z0-9]{1,16}$/;

export function validCodePrefix(prefix) {
    return CODE_PREFIX_PATTERN.test(String(prefix || '').trim());
}

// Blank, null and zero all mean "no fixed length".
export function hasFixedLength(codeLength) {
    return !(codeLength === null || codeLength === undefined || String(codeLength).trim() === ''
        || Number(codeLength) === 0);
}

// 'not_whole_number' | 'too_long' | 'too_short' | null
export function codeLengthProblem(prefix, codeLength) {
    if (!hasFixedLength(codeLength)) return null;
    const length = Number(codeLength);
    if (!Number.isInteger(length) || length < 0) return 'not_whole_number';
    if (length > MAX_CODE_LENGTH_DERIVED) return 'too_long';
    if (length <= String(prefix || '').trim().length) return 'too_short';
    return null;
}

export function accountDigitsThatFit(prefix, codeLength) {
    if (!hasFixedLength(codeLength) || codeLengthProblem(prefix, codeLength)) return null;
    return Number(codeLength) - String(prefix || '').trim().length;
}

// { code } or { problem: 'account_not_digits' | 'account_too_many_digits' | 'account_too_long' | a length problem }
export function buildDerivedCode(prefix, account, codeLength) {
    const cleanPrefix = String(prefix || '').trim();
    const cleanAccount = String(account || '').trim();
    if (!hasFixedLength(codeLength)) return { code: `${cleanPrefix}${cleanAccount}` };

    const lengthProblem = codeLengthProblem(cleanPrefix, codeLength);
    if (lengthProblem) return { problem: lengthProblem };
    if (!/^[1-9]\d*$/.test(cleanAccount)) return { problem: 'account_not_digits' };
    if (cleanAccount.length > MAX_ACCOUNT_DIGITS) return { problem: 'account_too_many_digits' };

    const room = Number(codeLength) - cleanPrefix.length;
    if (cleanAccount.length > room) return { problem: 'account_too_long' };
    return { code: `${cleanPrefix}${'0'.repeat(room - cleanAccount.length)}${cleanAccount}` };
}
